import { GAME_CONSTANTS } from '@tankworld/shared';
import { AMMUNITION_CATALOG, ImpactResolver, STANDARD_TANK_ARMOR_PROFILES, } from '@tankworld/armor-physics';
import { distanceVec3 } from './math.js';
export class ProjectileSystem {
    collisionSystem;
    projectiles = [];
    nextId = 1;
    impactResolver = new ImpactResolver();
    constructor(collisionSystem) {
        this.collisionSystem = collisionSystem;
    }
    spawn(ownerId, muzzlePos, muzzleDir, ownerVelocity, ammoId = 'ap_3bm42') {
        const ammoConfig = AMMUNITION_CATALOG[ammoId] || AMMUNITION_CATALOG.ap_3bm42;
        const speedScale = GAME_CONSTANTS.PROJECTILE_SPEED / 900;
        const simSpeed = ammoConfig.muzzleVelocityMs * speedScale;
        const velocity = {
            x: muzzleDir.x * simSpeed + ownerVelocity.x * 0.3,
            y: muzzleDir.y * simSpeed + ownerVelocity.y * 0.3,
            z: muzzleDir.z * simSpeed + ownerVelocity.z * 0.3,
        };
        const proj = {
            id: `proj-${this.nextId++}-${Date.now().toString(36)}`,
            ownerId,
            position: { ...muzzlePos },
            velocity,
            spawnTime: Date.now(),
            lifeTime: 0,
            ammoId: ammoConfig.id,
            bounces: 0,
        };
        this.projectiles.push(proj);
        return proj;
    }
    update(dt, obstacles, vehicles, events) {
        const gravity = GAME_CONSTANTS.PROJECTILE_GRAVITY;
        const targets = Array.from(vehicles.values())
            .filter((v) => v.isAlive)
            .map((v) => ({ id: v.id, position: v.position, radius: GAME_CONSTANTS.TANK_WIDTH * 0.6 }));
        const remainingProjectiles = [];
        for (const proj of this.projectiles) {
            proj.lifeTime += dt;
            // Ballistic step
            const prevPos = { ...proj.position };
            proj.velocity.y -= gravity * dt;
            const nextPos = {
                x: proj.position.x + proj.velocity.x * dt,
                y: proj.position.y + proj.velocity.y * dt,
                z: proj.position.z + proj.velocity.z * dt,
            };
            // Raycast segment test for continuous collision detection (CCD)
            const hit = this.collisionSystem.raycastSegment(prevPos, nextPos, obstacles, targets, proj.ownerId);
            if (hit.hit) {
                // Impact occurred!
                const impactPoint = hit.point;
                const attacker = vehicles.get(proj.ownerId);
                // 1. Direct hit with armor physics resolution
                if (hit.targetId) {
                    const victim = vehicles.get(hit.targetId);
                    if (victim && victim.isAlive) {
                        const ammoConfig = AMMUNITION_CATALOG[proj.ammoId] || AMMUNITION_CATALOG.ap_3bm42;
                        const armorHit = victim.raycastArmor(prevPos, nextPos);
                        let zone;
                        let surfaceNormal;
                        let actualHitPoint = impactPoint;
                        if (armorHit) {
                            zone = armorHit.zone;
                            surfaceNormal = armorHit.surfaceNormal;
                            actualHitPoint = armorHit.hitPoint;
                        }
                        else {
                            // Fallback normal pointing outwards from vehicle center
                            const dx = impactPoint.x - victim.position.x;
                            const dy = impactPoint.y - victim.position.y;
                            const dz = impactPoint.z - victim.position.z;
                            const dLen = Math.hypot(dx, dy, dz) || 1;
                            surfaceNormal = { x: dx / dLen, y: dy / dLen, z: dz / dLen };
                            zone =
                                victim.armorProfile?.zones[0] ||
                                    STANDARD_TANK_ARMOR_PROFILES.mbt_standard.zones[0];
                        }
                        const vLen = Math.hypot(proj.velocity.x, proj.velocity.y, proj.velocity.z) || 1;
                        const terminalVelocityVec = {
                            x: (proj.velocity.x / vLen) * ammoConfig.muzzleVelocityMs,
                            y: (proj.velocity.y / vLen) * ammoConfig.muzzleVelocityMs,
                            z: (proj.velocity.z / vLen) * ammoConfig.muzzleVelocityMs,
                        };
                        const impact = this.impactResolver.resolveImpact(actualHitPoint, ammoConfig, terminalVelocityVec, zone, surfaceNormal);
                        const totalDamage = impact.gameplayDamage + (impact.spallDamage || 0);
                        const destroyed = victim.applyDamage(totalDamage);
                        events.push({
                            id: `evt-hit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                            type: 'hit',
                            attackerId: proj.ownerId,
                            attackerName: attacker?.name,
                            victimId: victim.id,
                            victimName: victim.name,
                            damage: totalDamage,
                            position: actualHitPoint,
                            timestamp: Date.now(),
                            impactOutcome: impact.result,
                            armorZoneName: impact.armorZoneName,
                            incidenceDeg: impact.incidenceAngleDeg,
                            effectiveThicknessMm: impact.effectiveThicknessMm,
                        });
                        if (destroyed) {
                            if (attacker) {
                                attacker.kills++;
                                attacker.score += 100;
                            }
                            events.push({
                                id: `evt-kill-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                                type: 'kill',
                                attackerId: proj.ownerId,
                                attackerName: attacker?.name,
                                victimId: victim.id,
                                victimName: victim.name,
                                position: actualHitPoint,
                                timestamp: Date.now(),
                            });
                        }
                        // Ricochet deflection dynamics
                        if (impact.isRicochet && proj.bounces < 2 && impact.deflectedDirection) {
                            proj.position = { ...actualHitPoint };
                            proj.velocity = {
                                x: impact.deflectedDirection.x * impact.velocityAfterMs,
                                y: impact.deflectedDirection.y * impact.velocityAfterMs,
                                z: impact.deflectedDirection.z * impact.velocityAfterMs,
                            };
                            proj.bounces++;
                            remainingProjectiles.push(proj);
                            continue; // Do not consume deflected projectile
                        }
                    }
                }
                // 2. Splash damage to any tanks within radius
                for (const [id, target] of vehicles.entries()) {
                    if (!target.isAlive)
                        continue;
                    if (hit.targetId && id === hit.targetId)
                        continue; // Already took direct hit
                    const dist = distanceVec3(impactPoint, target.position);
                    if (dist <= GAME_CONSTANTS.SPLASH_RADIUS) {
                        const factor = 1 - dist / GAME_CONSTANTS.SPLASH_RADIUS;
                        const splashDmg = Math.round(GAME_CONSTANTS.SPLASH_DAMAGE * factor);
                        if (splashDmg > 0) {
                            const destroyed = target.applyDamage(splashDmg);
                            events.push({
                                id: `evt-splash-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                                type: 'hit',
                                attackerId: proj.ownerId,
                                attackerName: attacker?.name,
                                victimId: target.id,
                                victimName: target.name,
                                damage: splashDmg,
                                position: target.position,
                                timestamp: Date.now(),
                            });
                            if (destroyed && attacker) {
                                attacker.kills++;
                                attacker.score += 100;
                            }
                        }
                    }
                }
                // Projectile consumed on hit
                continue;
            }
            // Check lifetime limit
            if (proj.lifeTime < GAME_CONSTANTS.PROJECTILE_LIFETIME) {
                proj.position = nextPos;
                remainingProjectiles.push(proj);
            }
        }
        this.projectiles = remainingProjectiles;
    }
    toStates() {
        return this.projectiles.map(p => ({
            id: p.id,
            ownerId: p.ownerId,
            position: { ...p.position },
            velocity: { ...p.velocity },
            spawnTime: p.spawnTime,
            lifeTime: p.lifeTime,
        }));
    }
    clear() {
        this.projectiles = [];
    }
}
//# sourceMappingURL=projectiles.js.map